import sdk from "@repo/api-sdk";
import { queryOptions, useQuery } from "@tanstack/react-query";
import { queryKeys } from "./queryKeys";

/** A product's categories, keyed by product id. */
export type ProductCategoriesMap = Record<
  string,
  Array<{ id: string; name: string }>
>;

/**
 * Categories for an explicit set of products.
 *
 * Cart line items carry no category (`StoreSellerCartLineItem` has only
 * product/variant identity and money fields), and the pricing endpoint the
 * comparison grid uses doesn't return them either - so the cart has to ask for
 * them separately to offer a category filter. `fields` is narrowed to just the
 * id and categories, since nothing else is needed here.
 */
export function productCategoriesQuery(productIds: string[]) {
  return queryOptions({
    queryKey: queryKeys.forProducts(productIds),
    queryFn: async (): Promise<ProductCategoriesMap> => {
      const { products } = await sdk.store.product.list({
        id: productIds,
        fields: "id,*categories",
        limit: productIds.length
      });

      const map: ProductCategoriesMap = {};
      for (const product of products) {
        map[product.id] = (product.categories ?? []).map((category) => ({
          id: category.id,
          name: category.name
        }));
      }
      return map;
    },
    enabled: productIds.length > 0,
    // Categories change far less often than cart contents.
    staleTime: 5 * 60 * 1000
  });
}

export function useProductCategories(productIds: string[]) {
  return useQuery(productCategoriesQuery(productIds));
}
